import React from 'react';

import {getPlotFontSize} from '../../util/plotHelpers/plotFontSize';
import type {Bar} from '../../util/plotHelpers/types';
import BarChart from '../vis/BarChart/BarChart';
import {type ArtifactVersionMetricBar} from './artifactVersionMetricAdapters';
import type {ArtifactVersionMetricPlotReady} from './artifactVersionMetricPlotModel';
import {ArtifactVersionMetricTooltip} from './ArtifactVersionMetricTooltip';

type ArtifactVersionMetricBarChartProps = {
  plot: ArtifactVersionMetricPlotReady;
  height: number;
};

export const ArtifactVersionMetricBarChart: React.FC<
  ArtifactVersionMetricBarChartProps
> = ({plot, height}) => {
  const {config, bars, yDomain} = plot;
  const [hoveredBar, setHoveredBar] =
    React.useState<ArtifactVersionMetricBar | null>(null);
  const [tooltipPos, setTooltipPos] = React.useState<{
    left: number;
    top: number;
  } | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleMouseOver = React.useCallback(
    (event: React.MouseEvent<SVGRectElement, MouseEvent>, bar: Bar) => {
      setHoveredBar(bar as ArtifactVersionMetricBar);
      const container = containerRef.current;
      if (container != null) {
        const rect = container.getBoundingClientRect();
        setTooltipPos({
          left: event.clientX - rect.left,
          top: event.clientY - rect.top,
        });
      }
    },
    []
  );
  const handleMouseOut = React.useCallback(() => {
    setHoveredBar(null);
    setTooltipPos(null);
  }, []);

  return (
    <div ref={containerRef} className="relative h-full w-full min-h-0">
      <BarChart
        bars={bars}
        vertical={config.barVertical === true}
        enableGridRows
        showAllLabels
        min={yDomain[0]}
        max={yDomain[1]}
        fontSize={getPlotFontSize('auto', height)}
        height={height}
        enableTooltip={false}
        mouseOver={handleMouseOver}
        mouseOut={handleMouseOut}
      />
      {hoveredBar?.artifactRow != null && tooltipPos != null && (
        <ArtifactVersionMetricTooltip
          left={tooltipPos.left}
          top={tooltipPos.top}
          metricName={config.metricName}
          row={hoveredBar.artifactRow}
        />
      )}
    </div>
  );
};
